import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DisplayDeviceType } from './displayDevice.service';

export interface PlayerItem {
    player_id?: number;
    name_psn?: string;
    name_gt7?: string;
    name_discord?: string;
    active?: number;
    /**
     * Stored size of the avatar, or null when the player has none. The image
     * itself is NOT in this payload — build its URL via `avatarImageUrl()`.
     */
    avatar_bytes?: number | null;
    /** Preferred driving device — references driving_controller.id, or null. */
    controller_id?: number | null;
    /** Preferred output device — a fixed type ('monitor' | 'tv' | 'beamer' | 'vr'), or null. */
    display_device?: DisplayDeviceType | null;
    /** Date of birth as an ISO date string ('YYYY-MM-DD'), or null. */
    birthday?: string | null;
    /** Date of the driver's earliest race ("Ligafahrer seit"), or null. */
    first_race_date?: string | null;
}

export interface PlayerDetailsRaces {
    best_lap?: string;
    car_id?: number;
    car_name?: string;
    circuit_id?: number;
    circuits_icon_small?: string;
    circuits_name?: string;
    day_real?: string;
    final_time?: string;
    group_name?: string;
    name_gt7?: string;
    name_psn?: string;
    player_id?: number;
    position_clean?: number;
    position_finished?: number;
    raceday_id?: number;
    raceresult_id?: number;
    season_id?: number;
    season_name?: string;
    season_points?: string;
    status?: string; // 'E' or 'S'
}

export interface PlayerStatsItem {
    name?: string;
    description?: string;
}

export interface PlayerIncidentsItem {
    description?: string;
    player_id?: number;
    raceday_id?: number;
    raceincident_id?: number;
    /** Penalty points subtracted from the season total; PHP may deliver it as a string. */
    penalty?: number | string | null;
}

export interface PointsTableItem {
    points_id?: number;
    position?: number;
    points_v1?: number;
    points_v2?: number;
    points_v3?: number;
    points_v4?: number | null;
}

/**
 * Per-season participation summary computed by the API. `is_complete` marks a
 * "100%" badge: a finished season in which the driver attended every raceday
 * that took place in their group(s).
 */
export interface SeasonParticipationInfo {
    season_id: number;
    season_name: string;
    season_number: number;
    race_count: number;
    total_races: number;
    is_complete: boolean;
    season_status: string;
}

export interface PlayerDetails {
    player_races?: PlayerDetailsRaces[];
    name_psn?: string;
    name_gt7?: string;
    name_discord?: string;
    player_stats?: PlayerStatsItem[];
    player_incidents?: PlayerIncidentsItem[];
    points_table?: PointsTableItem[];
    season_participation?: SeasonParticipationInfo[];
}

/**
 * Stable static link to a driver's participation badge for one season, served
 * as a scalable SVG (api/badge.php) that can be embedded in other applications.
 * `size` is the rendered height in px (aspect kept); omit for size-agnostic.
 */
export function badgeImageUrl(playerId: number, seasonId: number, size?: number): string {
    const base = `${environment.apiBaseUrl}/api/badge.php?player_id=${playerId}&season_id=${seasonId}`;
    return size ? `${base}&size=${size}` : base;
}

/**
 * Direct URL of a player's avatar (binary response, long-lived browser cache),
 * or null when the player has none. `avatar_bytes` from players.php busts the
 * cache when the avatar changes.
 */
export function avatarImageUrl(player: PlayerItem): string | null {
    if (player.player_id == null || player.avatar_bytes == null) return null;
    return `${environment.apiBaseUrl}/api/avatar.php?player_id=${player.player_id}&v=${player.avatar_bytes}`;
}

@Injectable()
export class PlayerService {
    private apiGetPlayerUrl = `${environment.apiBaseUrl}/api/players.php`;
    private apiGetPlayerDetailsUrl = `${environment.apiBaseUrl}/api/playerDetails.php`;
    private apiAvatarUrl = `${environment.apiBaseUrl}/api/avatar.php`;

    constructor(private http: HttpClient) {}

    getPlayerLarge(): Observable<PlayerItem[]> {
        return this.http.get<PlayerItem[]>(this.apiGetPlayerUrl);
    }

    getPlayerDetails(player_id: string): Observable<PlayerDetails> {
        const params = new HttpParams().set('player_id', player_id);
        return this.http.get<PlayerDetails>(this.apiGetPlayerDetailsUrl, { params });
    }

    createPlayer(player: PlayerItem): Observable<PlayerItem> {
        return this.http.post<PlayerItem>(this.apiGetPlayerUrl, player);
    }

    updatePlayer(player: PlayerItem): Observable<{ success: boolean }> {
        return this.http.put<{ success: boolean }>(this.apiGetPlayerUrl, player);
    }

    deletePlayer(player_id: number): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(this.apiGetPlayerUrl, { body: { player_id } });
    }

    /** Upload an avatar image for the logged-in member's linked player. */
    uploadAvatar(file: File): Observable<{ avatar: string }> {
        const form = new FormData();
        form.append('avatar', file);
        return this.http.post<{ avatar: string }>(this.apiAvatarUrl, form);
    }

    /** Remove the avatar of the logged-in member's linked player. */
    deleteAvatar(): Observable<{ avatar: null }> {
        return this.http.delete<{ avatar: null }>(this.apiAvatarUrl);
    }
}
